<template>
  <div class="mng-container">
    <!-- 页头 -->
    <header class="mng-header">
      <h1 class="mng-title">笔记管理</h1>
      <span class="mng-sub">数据源：biji 主页（proj.biji）</span>
      <span class="mng-user" v-if="me.username">{{ me.username }} · {{ me.role }}</span>
      <div class="mng-actions">
        <router-link to="/biji" class="mng-link">返回主页</router-link>
        <el-button size="small" @click="logout">退出登录</el-button>
        <el-button size="small" type="primary" icon="el-icon-plus" @click="openCreate">新增</el-button>
      </div>
    </header>

    <!-- 字段筛选区：每个数据库字段一个过滤项 -->
    <div class="mng-filters">
      <el-input v-model="filters.id" size="small" clearable placeholder="ID" class="f-id" @keyup.enter.native="search" />
      <el-select
        v-model="filters.title"
        size="small"
        clearable
        filterable
        placeholder="标题"
        class="f-w"
        @change="search"
        @clear="search"
      >
        <el-option
          v-for="t in titleOptions"
          :key="t"
          :label="t"
          :value="t"
        />
      </el-select>
      <el-input v-model="filters.subtitle" size="small" clearable placeholder="副标题" class="f-w" @keyup.enter.native="search" />
      <el-select
        v-model="filters.author"
        size="small"
        clearable
        filterable
        placeholder="作者"
        class="f-w"
        @change="search"
        @clear="search"
      >
        <el-option
          v-for="a in authorOptions"
          :key="a"
          :label="a"
          :value="a"
        />
      </el-select>
      <el-input v-model="filters.content" size="small" clearable placeholder="内容" class="f-w" @keyup.enter.native="search" />
      <el-select v-model="filters.order" size="small" class="f-s" @change="search">
        <el-option label="新→旧" value="desc"></el-option>
        <el-option label="旧→新" value="asc"></el-option>
      </el-select>
      <el-button size="small" type="primary" icon="el-icon-search" :loading="loading" @click="search">查询</el-button>
      <el-button size="small" @click="resetFilters">重置</el-button>
    </div>

    <el-alert v-if="error" type="error" :title="error" show-icon :closable="false" class="mng-error" />
    <div v-if="loadingOptions" class="mng-loading-options">筛选选项加载中…</div>

    <!-- 列表 -->
    <div class="mng-table-wrap" v-loading="loading">
      <table class="mng-table">
        <thead>
          <tr>
            <th class="c-title">标题</th>
            <th class="c-subtitle">副标题</th>
            <th class="c-author">作者</th>
            <th class="c-content">内容</th>
            <th class="c-time">收录时间</th>
            <th class="c-time">录入时间</th>
            <th class="c-op">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in items" :key="(row.id || '') + idx">
            <td class="c-title" data-label="标题">{{ row.title }}</td>
            <td class="c-subtitle" data-label="副标题">{{ row.subtitle || '—' }}</td>
            <td class="c-author" data-label="作者">{{ row.author || '—' }}</td>
            <td class="c-content" data-label="内容" :title="row.content">{{ excerpt(row.content, 60) }}</td>
            <td class="c-time" data-label="收录时间">{{ tsToDate(row.createtime) }}</td>
            <td class="c-time" data-label="录入时间">{{ tsToDate(row.recordtime) }}</td>
            <td class="c-op" data-label="操作">
              <el-button size="mini" @click="openEdit(row)">编辑</el-button>
              <el-button size="mini" type="danger" @click="removeItem(row)">删除</el-button>
            </td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="7" class="mng-empty">暂无数据</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分页 -->
    <div class="mng-pager">
      <el-pagination
        background
        :layout="pagerLayout"
        :pager-count="isMobile ? 5 : 7"
        :total="total"
        :current-page.sync="page"
        :page-size.sync="pageSize"
        :page-sizes="[10, 20, 50]"
        @current-change="query"
        @size-change="onSizeChange"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="form.id ? '编辑笔记' : '新增笔记'"
      :visible.sync="dialogVisible"
      :width="dialogWidth"
      custom-class="mng-form-dialog"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :label-position="isMobile ? 'top' : 'right'" label-width="90px" size="small">
        <el-form-item label="标题" required>
          <el-input v-model="form.title" placeholder="标题" />
        </el-form-item>
        <el-form-item label="副标题">
          <el-input v-model="form.subtitle" placeholder="副标题（可空）" />
        </el-form-item>
        <el-form-item label="作者">
          <el-input v-model="form.author" placeholder="作者（可空，缺省我）" />
        </el-form-item>
        <el-form-item label="内容" required>
          <el-input type="textarea" :rows="8" v-model="form.content" placeholder="笔记内容，多行请换行" />
        </el-form-item>
        <el-form-item label="收录时间">
          <el-date-picker
            v-model="form.createMs"
            type="datetime"
            value-format="timestamp"
            placeholder="收录时间"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="录入时间">
          <el-date-picker
            v-model="form.recordMs"
            type="datetime"
            value-format="timestamp"
            placeholder="录入时间"
            style="width: 100%"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import axios from "axios";
import { UseUrl } from "../../utils/config";

// 登录/鉴权复用 shicigame；数据读写走 dataservice 的 proj.biji
const AUTH_BASE = UseUrl + "/shicigame";
const BASE = UseUrl + "/biji/mng";

export default {
  name: "bijimng",
  data() {
    return {
      me: { username: "", role: "" },
      filters: { id: "", title: "", subtitle: "", author: "", content: "", order: "desc" },
      // 列表筛选项：进入页面时一次性预加载
      titleOptions: [],
      authorOptions: [],
      loadingOptions: false,
      items: [],
      total: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      saving: false,
      error: "",
      dialogVisible: false,
      windowWidth: (typeof window !== "undefined" ? window.innerWidth : 1280),
      form: { id: "", title: "", subtitle: "", author: "", content: "", createMs: null, recordMs: null }
    }
  },
  computed: {
    // 窄屏（手机）判定：用于切换弹窗宽度、表单标签位置与分页布局
    isMobile() {
      return this.windowWidth <= 768;
    },
    dialogWidth() {
      return this.isMobile ? "94%" : "680px";
    },
    pagerLayout() {
      return this.isMobile ? "total, prev, pager, next" : "total, prev, pager, next, sizes";
    }
  },
  methods: {
    token() {
      return String(localStorage.getItem("shicigame_token") || "");
    },
    // 登录校验（借用 shicigame）：无 token 或失效 → 跳登录页
    checkAuth() {
      if (!this.token()) {
        this.$router.replace({ path: "/shicilogin", query: { redirect: "/bijimng" } });
        return;
      }
      var that = this;
      Vue.axios.post(AUTH_BASE + "/auth/me").then((response) => {
        var r = response.data || {};
        if (!r.loggedIn) {
          that.$router.replace({ path: "/shicilogin", query: { redirect: "/bijimng" } });
          return;
        }
        that.me = r.user || { username: "", role: "" };
        if (that.me.role !== "admin") {
          that.error = "当前账号无管理权限（需要 admin）";
        }
        that.query();
      }).catch(() => {
        that.$router.replace({ path: "/shicilogin", query: { redirect: "/bijimng" } });
      });
    },
    logout() {
      localStorage.removeItem("shicigame_token");
      localStorage.removeItem("shicigame_user");
      this.$router.replace({ path: "/shicilogin" });
    },
    query() {
      this.loading = true;
      this.error = "";
      var that = this;
      Vue.axios.post(BASE + "/list", {
        page: this.page,
        pageSize: this.pageSize,
        order: this.filters.order,
        id: this.filters.id,
        title: this.filters.title,
        subtitle: this.filters.subtitle,
        author: this.filters.author,
        content: this.filters.content
      }).then((response) => {
        var r = response.data || {};
        that.items = r.list || [];
        that.total = r.total || 0;
        that.loading = false;
      }).catch((err) => {
        that.loading = false;
        that.error = "查询失败: " + (err && err.message ? err.message : "未知错误");
      });
    },
    search() {
      this.page = 1;
      this.query();
    },
    resetFilters() {
      this.filters = { id: "", title: "", subtitle: "", author: "", content: "", order: "desc" };
      this.search();
    },
    onSizeChange() {
      this.page = 1;
      this.query();
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    openCreate() {
      this.form = { id: "", title: "", subtitle: "", author: "", content: "", createMs: null, recordMs: null };
      this.dialogVisible = true;
    },
    openEdit(row) {
      this.form = {
        id: row.id || "",
        title: row.title || "",
        subtitle: row.subtitle || "",
        author: row.author || "",
        content: row.content || "",
        createMs: row.createtime ? row.createtime * 1000 : null,
        recordMs: row.recordtime ? row.recordtime * 1000 : null
      };
      this.dialogVisible = true;
    },
    save() {
      if (!this.form.title.trim() || !this.form.content.trim()) {
        this.$message.warning("标题和内容不能为空");
        return;
      }
      this.saving = true;
      var that = this;
      var body = {
        id: this.form.id,
        title: this.form.title,
        subtitle: this.form.subtitle,
        author: this.form.author,
        content: this.form.content,
        createtime: this.form.createMs ? Math.floor(this.form.createMs / 1000) : 0,
        recordtime: this.form.recordMs ? Math.floor(this.form.recordMs / 1000) : 0
      };
      Vue.axios.post(BASE + "/save", body).then((response) => {
        var r = response.data || {};
        that.saving = false;
        if (r.success === false) {
          that.$message.error(r.message || "保存失败");
          return;
        }
        that.$message.success(r.message || "保存成功");
        that.dialogVisible = false;
        that.query();
      }).catch((err) => {
        that.saving = false;
        that.$message.error("保存失败: " + (err && err.message ? err.message : ""));
      });
    },
    removeItem(row) {
      var that = this;
      this.$confirm(`确定删除「${row.title}」？此操作不可恢复。`, "删除确认", {
        confirmButtonText: "删除",
        cancelButtonText: "取消",
        type: "warning"
      }).then(() => {
        Vue.axios.post(BASE + "/delete", { id: row.id }).then((response) => {
          var r = response.data || {};
          if (r.success === false) {
            that.$message.error(r.message || "删除失败");
            return;
          }
          that.$message.success("已删除");
          if (that.items.length === 1 && that.page > 1) {
            that.page -= 1;
          }
          that.query();
        }).catch((err) => {
          that.$message.error("删除失败: " + (err && err.message ? err.message : ""));
        });
      }).catch(() => {});
    },
    shortId(id) {
      if (!id) return "—";
      return id.length > 12 ? id.slice(0, 12) + "…" : id;
    },
    excerpt(text, n) {
      if (!text) return "";
      const oneLine = String(text).replace(/[\n\r]+/g, " / ");
      return oneLine.length > n ? oneLine.slice(0, n) + "…" : oneLine;
    },
    tsToDate(ts) {
      if (!ts) return "—";
      const d = new Date(ts * 1000);
      const p = v => (v < 10 ? "0" + v : v);
      return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + " " + p(d.getHours()) + ":" + p(d.getMinutes());
    },
    // 预加载筛选下拉选项：title(主页 /biji/categories) + author(/biji/mng/authors)
    loadOptions() {
      var that = this;
      this.loadingOptions = true;
      Promise.all([
        axios.post(UseUrl + "/biji/categories", {}).then(function (r) {
          var data = (r && r.data) || {};
          that.titleOptions = Array.isArray(data.categories) ? data.categories.filter(Boolean) : [];
        }),
        axios.post(BASE + "/authors", {}).then(function (r) {
          var data = (r && r.data) || {};
          that.authorOptions = Array.isArray(data.authors) ? data.authors.filter(Boolean) : [];
        })
      ]).then(function () {
        that.loadingOptions = false;
      }).catch(function (err) {
        that.loadingOptions = false;
        var msg = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : (err && err.message) || "未知错误";
        that.$message && that.$message.error("筛选选项加载失败：" + msg);
        console.error("loadOptions failed", err);
      });
    }
  },
  mounted() {
    this.checkAuth();
    this.loadOptions();
    window.addEventListener("resize", this.onResize, false);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize, false);
  }
}
</script>

<style scoped>
.mng-container {
  --paper: #f5f1e5;
  --paper-card: #fdfaf1;
  --ink: #26221a;
  --ink-soft: #5c554a;
  --ink-faint: #99917f;
  --hairline: #ddd3ba;
  --cinnabar: #b13a26;

  max-width: 1280px;
  margin: 0 auto;
  padding: 24px 20px 60px;
  font-family: "Noto Serif SC", "Songti SC", STSong, SimSun, serif;
  color: var(--ink);
}

.mng-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--hairline);
  margin-bottom: 16px;
}

.mng-title {
  margin: 0;
  font-size: 26px;
  letter-spacing: 6px;
}

.mng-sub {
  color: var(--ink-faint);
  font-size: 12px;
  letter-spacing: 1px;
}

.mng-user {
  color: var(--ink-faint);
  font-size: 13px;
  letter-spacing: 1px;
}

.mng-actions {
  margin-left: auto;
  display: flex;
  gap: 10px;
  align-items: center;
}

.mng-link {
  color: var(--cinnabar);
  text-decoration: none;
  font-size: 13px;
  letter-spacing: 2px;
}

/* 筛选区 */
.mng-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 12px 14px;
  background: rgba(253, 250, 241, 0.85);
  border: 1px solid var(--hairline);
  border-radius: 4px;
  margin-bottom: 14px;
}

.f-w { width: 150px; }
.f-s { width: 110px; }
.f-id { width: 170px; }

.mng-error {
  margin-bottom: 12px;
}

.mng-loading-options {
  color: var(--ink-faint);
  font-size: 12px;
  letter-spacing: 1px;
  padding: 6px 0 10px;
}

/* 表格 */
.mng-table-wrap {
  background: var(--paper-card);
  border: 1px solid var(--hairline);
  border-radius: 4px;
  overflow-x: auto;
}

.mng-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.mng-table th {
  background: rgba(233, 224, 200, 0.45);
  color: var(--ink-soft);
  font-weight: 600;
  letter-spacing: 2px;
  padding: 10px 10px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 1px solid var(--hairline);
}

.mng-table td {
  padding: 9px 10px;
  border-bottom: 1px solid rgba(221, 211, 186, 0.5);
  vertical-align: middle;
}

.mng-table tbody tr:hover {
  background: rgba(177, 58, 38, 0.04);
}

.c-id { max-width: 130px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ink-faint); font-size: 12px; }
.c-title { min-width: 100px; font-weight: 600; letter-spacing: 1px; }
.c-subtitle { width: 110px; color: var(--ink-soft); }
.c-author { width: 100px; }
.c-content { max-width: 360px; color: var(--ink-soft); }
.c-time { width: 130px; color: var(--ink-faint); font-size: 12px; white-space: nowrap; }
.c-op { width: 140px; white-space: nowrap; }

.mng-empty {
  text-align: center;
  color: var(--ink-faint);
  padding: 40px 0 !important;
  letter-spacing: 4px;
}

.mng-pager {
  margin-top: 16px;
  text-align: right;
}

/* ============ 移动端适配 ============ */
@media (max-width: 768px) {
  .mng-container {
    padding: 16px 12px 44px;
  }

  /* 页头：标题与用户名同行，操作区整行右对齐 */
  .mng-header {
    flex-wrap: wrap;
    gap: 8px 10px;
  }

  .mng-title {
    font-size: 20px;
    letter-spacing: 3px;
    flex: 1 1 auto;
  }

  .mng-sub {
    display: none;
  }

  .mng-user {
    font-size: 12px;
    margin-left: auto;
  }

  .mng-actions {
    margin-left: 0;
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  /* 筛选区：输入两列排布，按钮整行等分 */
  .mng-filters {
    padding: 10px;
    gap: 8px;
  }

  .f-w,
  .f-s,
  .f-id {
    width: calc(50% - 4px);
  }

  .mng-filters > .el-button {
    flex: 1 1 0;
    margin-left: 0 !important;
  }

  /* 表格：转为卡片式堆叠，避免横向滚动 */
  .mng-table-wrap {
    overflow-x: visible;
    background: transparent;
    border: none;
  }

  .mng-table thead {
    display: none;
  }

  .mng-table,
  .mng-table tbody,
  .mng-table tr,
  .mng-table td {
    display: block;
    width: 100%;
    box-sizing: border-box;
  }

  .mng-table tr {
    background: var(--paper-card);
    border: 1px solid var(--hairline);
    border-radius: 8px;
    margin-bottom: 10px;
    padding: 4px 0;
  }

  .mng-table td {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding: 8px 12px;
    border-bottom: 1px dashed rgba(221, 211, 186, 0.7);
    text-align: right;
    max-width: none;
  }

  .mng-table td:last-child {
    border-bottom: none;
  }

  .mng-table td::before {
    content: attr(data-label);
    flex: 0 0 auto;
    padding-top: 1px;
    font-size: 12px;
    letter-spacing: 2px;
    color: var(--ink-faint);
    text-align: left;
  }

  /* 长文本（内容）左右并排会挤压，改为上下堆叠左对齐 */
  .mng-table td.c-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: left;
    white-space: normal;
  }

  .mng-table td.c-op {
    justify-content: flex-end;
    gap: 8px;
  }

  .mng-table td.c-op::before {
    margin-right: auto;
  }

  .mng-table td.c-op .el-button + .el-button {
    margin-left: 0;
  }

  .mng-table .mng-empty {
    display: block;
    text-align: center;
    border-bottom: none;
  }

  .mng-table .mng-empty::before {
    content: none;
  }

  /* 分页居中 */
  .mng-pager {
    text-align: center;
  }
}
</style>

<style>
/* el-dialog 的容器不在 scoped 作用域内，用全局规则收紧移动端留白（custom-class 指定） */
@media (max-width: 768px) {
  .mng-form-dialog {
    margin: 6vh auto 30px !important;
  }

  .mng-form-dialog .el-dialog__header {
    padding: 14px 14px 8px;
  }

  .mng-form-dialog .el-dialog__body {
    padding: 12px 14px 16px;
  }

  .mng-form-dialog .el-dialog__footer {
    padding: 10px 14px 14px;
  }

  /* 分页项过多时允许换行，避免超出窄屏宽度 */
  .mng-pager .el-pagination {
    white-space: normal;
  }
}
</style>